import type { App } from 'vue';
import PowerduckState from '@powerduck/app/powerduck-state';

const FALLBACK_PAGINATIONS = [10, 25, 50, 100, 250, 500, 1000, -1];
const PATCHED = '__iuMassOpPageLength';

/**
 * PowerDuck's DataTable renders the records-per-page picker only while the
 * mass-operation checkboxes are hidden: once "Hromadne operacie" is toggled on,
 * `renderMassOpButtons()` takes over the picker's slot and the page size can
 * no longer be changed (event-orders, attendee-list, ...). We can't edit
 * PowerDuck, so wrap that method and render the same picker right before the
 * mass-op buttons. The picker is added exactly when the original one is hidden
 * (the mass-op content is non-null), so the two never render together.
 *
 * Patched from a global mixin instead of importing DataTable here: pages are
 * lazy-loaded and DataTable is not part of the entry bundle. Vue reads
 * `methods` from the merged (per-component-type cached) options right after
 * `beforeCreate`, so patching `$options.methods` there applies to every
 * DataTable instance, once per app.
 */
export const DataTableMassOpPageLengthPlugin = {
	install(app: App) {
		app.mixin({
			beforeCreate() {
				const methods = this.$options.methods;
				if (methods == null || methods[PATCHED] || typeof methods.renderMassOpButtons !== 'function' || typeof methods.handlePaginationLengthChanged !== 'function') {
					return;
				}

				methods.renderMassOpButtons = wrapRenderMassOpButtons(methods.renderMassOpButtons);
				methods[PATCHED] = true;
			},
		});
	},
};

function wrapRenderMassOpButtons(original: (h: any) => any) {
	return function (this: any, h: any) {
		const massOpContent = original.call(this, h);
		if (massOpContent == null) {
			return massOpContent;
		}

		const paginations: number[] = typeof this.getEffectivePaginations === 'function'
			? this.getEffectivePaginations()
			: (this.paginations || FALLBACK_PAGINATIONS);

		return [
			<div class="dt-pagination-length dt-massop-page-length">
				<select onChange={e => this.handlePaginationLengthChanged(Number((e.target as HTMLSelectElement).value))}>
					{paginations.map(item => (
						<option value={item} selected={this.getPaginationLength() == item}>
							{item != -1 ? item.toString() : PowerduckState.getResourceValue('all')}
						</option>
					))}
				</select>

				<span>{PowerduckState.getResourceValue('recordsDtLabel')}</span>
			</div>,
			massOpContent,
		];
	};
}
